"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RemoteAudioController = exports.LocalAudioController = void 0;
const detect_browser_1 = require("detect-browser");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const logger_1 = require("../../util/logger");
const toolbox_1 = require("@streem/toolbox");
const filter_null_1 = require("../../util/rx/filter_null");
const take_first_emitted_1 = require("../../util/rx/take_first_emitted");
const analytics_1 = require("@streem/analytics");
class AudioController {
    constructor(config) {
        var _a;
        this.config = config;
        this.log = logger_1.logger.extend('sdk:audio_controller');
        this.subscriptions = new toolbox_1.SubscriptionManager();
        this.audioTrackWallItem = config.wallItem.pipe((0, operators_1.shareReplay)(1));
        const trackSourceSub = config.trackSource.subscribe(audioTrack => {
            if (audioTrack) {
                this.audioTrack = audioTrack;
            }
        });
        this.subscriptions.add(trackSourceSub);
        this.connected = config.trackSource.pipe((0, operators_1.map)(track => !!track), (0, operators_1.shareReplay)(1));
        this.enabled = config.trackSource.pipe((0, operators_1.switchMap)(audioTrack => {
            if (audioTrack) {
                return audioTrack.enabled.pipe((0, operators_1.tap)(enabled => this.log.info(`Track of type: audio has been ${enabled ? 'enabled' : 'disabled'} for participant: ${config.userId.toString()}`)));
            }
            else {
                return (0, rxjs_1.of)(false);
            }
        }), (0, operators_1.shareReplay)(1));
        this.started = config.trackSource.pipe((0, operators_1.switchMap)(audioTrack => {
            if (audioTrack) {
                return audioTrack.started.pipe((0, operators_1.tap)(started => this.log.info(`Track of type: audio has ${started ? 'started' : 'stopped'} for participant: ${config.userId.toString()}`)));
            }
            else {
                return (0, rxjs_1.of)(false);
            }
        }), (0, operators_1.shareReplay)(1));
        this.audioLevel = config.trackSource.pipe((0, operators_1.switchMap)(audioTrack => {
            if (audioTrack) {
                audioTrack.audioLevel.pipe((0, operators_1.tap)(audioLevel => this.log.info(`Audio level for ${audioTrack.trackLabel} is: ${audioLevel}`)));
                return audioTrack.audioLevel;
            }
            else {
                return (0, rxjs_1.of)(0);
            }
        }), (0, operators_1.shareReplay)(1));
        this.backgrounded = (_a = this.audioTrackWallItem) === null || _a === void 0 ? void 0 : _a.pipe((0, operators_1.map)(item => !!(item === null || item === void 0 ? void 0 : item.backgrounded)), (0, operators_1.shareReplay)(1));
    }
    disconnect() {
        this.subscriptions.unsubscribeAll();
    }
}
class LocalAudioController extends AudioController {
    setEnabled(enable) {
        if (this.audioTrack) {
            this.audioTrack.setEnabled(enable);
            (0, analytics_1.recordMicrophoneStatusSet)(enable, this.config.roomId);
            this.log.info(`Track of type: audio has been turned ${enable ? 'ON' : 'OFF'} for participant: ${this.config.userId.toString()}`);
        }
    }
    restart(constraints) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const success = yield ((_a = this.audioTrack) === null || _a === void 0 ? void 0 : _a.restart(constraints));
            if (!success) {
                this.log.error(`Failed to successfully restart audio track for ${this.config.userId.toString()}`);
            }
            return success !== null && success !== void 0 ? success : false;
        });
    }
    setBackgrounded(backgrounded) {
        return __awaiter(this, void 0, void 0, function* () {
            const wallItem = yield (0, take_first_emitted_1.takeFirstEmitted)(this.audioTrackWallItem);
            this.config.roomWall.update(this.config.roomWall.builder.buildTrackWallItem('audioTrack', Object.assign(Object.assign({}, wallItem), { backgrounded })));
            this.log.info(`audioTrack backgrounded value set to ${backgrounded} for:  ${this.config.userId.toString()}`);
        });
    }
}
exports.LocalAudioController = LocalAudioController;
class RemoteAudioController extends AudioController {
    constructor(config) {
        super(config);
        this.webkitAudioFix196539(this.config.trackSource.pipe((0, filter_null_1.filterNull)(), (0, operators_1.switchMap)(track => track.audioTrack), (0, filter_null_1.filterNull)()));
        this.muted = this.audioTrackWallItem.pipe((0, operators_1.map)(item => !(item === null || item === void 0 ? void 0 : item.active)), (0, operators_1.shareReplay)(1));
    }
    selectSpeaker(deviceId) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            return (_a = this.audioTrack) === null || _a === void 0 ? void 0 : _a.selectSpeaker(deviceId, this.config.userId.toString());
        });
    }
    /**
     * This performs an audio fix for HTMLMediaElements in Safari.
     *
     * Please do not delete this until the webkit bug is fixed.
     * ~Alex
     * https://github.com/twilio/twilio-video.js/issues/922#issuecomment-607038582
     * https://bugs.webkit.org/show_bug.cgi?id=196539
     */
    webkitAudioFix196539(mediaElement) {
        mediaElement.pipe((0, operators_1.take)(1)).subscribe(mediaElement => {
            const browser = (0, detect_browser_1.detect)();
            if (!browser || !mediaElement) {
                return;
            }
            // We must stop and resume audio on safari.
            // https://github.com/streem/streem-app/pull/5498/files#diff-81ad430dd509a04ba2ead2ae56ad21c7ca32ea32b24eb9a9e7ec0d387c452f15R51
            if (browser.name == 'safari' || browser.name == 'ios') {
                // once the audio element has started playing
                (0, toolbox_1.once)(mediaElement, 'play')
                    .then(() => {
                    // wait for the audio element to come to a complete pause.
                    // We'll confirm it has paused by resolving its "pause" event https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/pause_event
                    const onPause = (0, toolbox_1.once)(mediaElement, 'pause');
                    mediaElement.pause();
                    logger_1.logger.info('Re-initializing audio for Safari volume fix - audioTrackEvent=paused');
                    return onPause;
                })
                    .then(() => {
                    // wait for the audio element resume playing
                    return mediaElement.play();
                })
                    .then(() => {
                    return void logger_1.logger.info('Re-initialized audio for Safari volume fix - audioTrackEvent=resumed');
                });
            }
        });
    }
}
exports.RemoteAudioController = RemoteAudioController;
//# sourceMappingURL=audio_controllers.js.map